/**
 * Encryption Manager: high-level orchestration of E2E encryption.
 *
 * Bridges sender-keys.ts (key generation/distribution) and
 * sframe-worker.ts (frame-level encrypt/decrypt) into a single API
 * that the connection bridge can call.
 */

import type { Identity, SenderKeyPair } from './types'
import type {
  SFrameWorkerCommand,
  SFrameInstallKeyCommand,
  SFrameSetLocalKeyIdCommand,
} from './crypto/sframe-worker'
import {
  rotateLocalKey,
  deriveSharedSecret,
  encryptSenderKey,
  decryptSenderKey,
  installReceivedKey,
  expirePeerKeys,
  removePeer,
  clearAllKeys,
  getLocalKey,
  getLocalEpoch,
} from './crypto/sender-keys'
import { participantIdToKeyId } from './crypto/identity'

// ============================================================
// Types
// ============================================================

export interface EncryptionPeer {
  peerId: string
  publicKey: CryptoKey // ECDH public key
  sharedSecret: CryptoKey // derived AES-GCM wrapping key
}

export interface AddPeerResult {
  /** Encrypted key payload for the new peer */
  newPeerPayload: EncryptedKeyPayload
  /** Re-key payloads for existing peers (forward secrecy rotation) */
  reKeyPayloads: Map<string, EncryptedKeyPayload>
}

export interface EncryptionManager {
  /** Add a peer, rotate key for forward secrecy, distribute to all */
  addEncryptedPeer(peerId: string, peerPublicKey: CryptoKey): Promise<AddPeerResult>
  /** Remove a peer and rotate local key (backward secrecy) */
  removeEncryptedPeer(peerId: string): Promise<Map<string, EncryptedKeyPayload>>
  /** Post a command to the SFrame worker */
  postWorkerCommand(cmd: SFrameWorkerCommand): void
  /** Get the SFrame worker instance */
  getWorker(): Worker | null
  /** Clean up everything */
  teardown(): void
}

/** Encrypted sender key payload ready for transport */
export interface EncryptedKeyPayload {
  epoch: number
  encryptedKey: ArrayBuffer
}

// ============================================================
// Module state
// ============================================================

let worker: Worker | null = null
let localIdentity: Identity | null = null
const peers = new Map<string, EncryptionPeer>()

// ============================================================
// Setup
// ============================================================

/**
 * Initialize E2E encryption for the local participant.
 *
 * 1. Spawns the SFrame Worker
 * 2. Generates the initial local sender key
 * 3. Installs it in the worker
 *
 * Returns the EncryptionManager for ongoing operations.
 */
export async function setupEncryption(
  identity: Identity,
): Promise<EncryptionManager> {
  // Tear down any previous session
  teardownEncryption()

  localIdentity = identity

  // Spawn SFrame worker
  worker = new Worker(
    new URL('./crypto/sframe-worker.ts', import.meta.url),
    { type: 'module' },
  )

  // Generate initial sender key
  const senderKey = await rotateLocalKey(identity.participantId)
  installKeyInWorker(identity.participantId, senderKey)
  setLocalKeyIdInWorker(senderKey.keyId)

  return {
    addEncryptedPeer,
    removeEncryptedPeer,
    postWorkerCommand,
    getWorker: () => worker,
    teardown: teardownEncryption,
  }
}

// ============================================================
// Peer Management
// ============================================================

/**
 * Add a peer: derive ECDH shared secret, rotate key for forward secrecy
 * if existing peers are present, distribute to all.
 *
 * Returns the new peer's payload and re-key payloads for existing peers.
 */
async function addEncryptedPeer(
  peerId: string,
  peerPublicKey: CryptoKey,
): Promise<AddPeerResult> {
  if (!localIdentity) throw new Error('Encryption not initialized')

  // Derive ECDH shared secret
  const sharedSecret = await deriveSharedSecret(
    localIdentity.exchangeKeyPair.privateKey,
    peerPublicKey,
  )

  const hadExistingPeers = peers.size > 0

  peers.set(peerId, { peerId, publicKey: peerPublicKey, sharedSecret })

  // Forward secrecy: rotate key when existing peers are present
  // so the new peer cannot decrypt prior traffic
  if (hadExistingPeers) {
    const newKey = await rotateLocalKey(localIdentity.participantId)
    installKeyInWorker(localIdentity.participantId, newKey)
    setLocalKeyIdInWorker(newKey.keyId)
  }

  // Encrypt current sender key for all peers (including new one)
  const localKey = getLocalKey()
  if (!localKey) throw new Error('No local sender key')

  const reKeyPayloads = new Map<string, EncryptedKeyPayload>()
  for (const [existingPeerId, peer] of peers) {
    if (existingPeerId === peerId) continue
    const encryptedKey = await encryptSenderKey(peer.sharedSecret, localKey.rawKey)
    reKeyPayloads.set(existingPeerId, {
      epoch: getLocalEpoch(),
      encryptedKey,
    })
  }

  // Encrypt for the new peer
  const newPeerEncryptedKey = await encryptSenderKey(sharedSecret, localKey.rawKey)

  return {
    newPeerPayload: {
      epoch: getLocalEpoch(),
      encryptedKey: newPeerEncryptedKey,
    },
    reKeyPayloads,
  }
}

/**
 * Remove a peer and rotate the local sender key for backward secrecy.
 *
 * 1. Expire the leaving peer's keys
 * 2. Remove them from our peer map
 * 3. Rotate local key
 * 4. Re-encrypt the new key for all remaining peers
 *
 * Returns a map of peerId -> encrypted key payload for redistribution.
 */
async function removeEncryptedPeer(
  peerId: string,
): Promise<Map<string, EncryptedKeyPayload>> {
  if (!localIdentity) throw new Error('Encryption not initialized')

  // Expire + remove the leaving peer's keys
  expirePeerKeys(peerId)
  removePeer(peerId)
  peers.delete(peerId)

  // Rotate local key (backward secrecy)
  const newKey = await rotateLocalKey(localIdentity.participantId)
  installKeyInWorker(localIdentity.participantId, newKey)
  setLocalKeyIdInWorker(newKey.keyId)

  // Re-encrypt new key for all remaining peers
  const reKeyPayloads = new Map<string, EncryptedKeyPayload>()
  for (const [remainingPeerId, peer] of peers) {
    const encryptedKey = await encryptSenderKey(peer.sharedSecret, newKey.rawKey)
    reKeyPayloads.set(remainingPeerId, {
      epoch: getLocalEpoch(),
      encryptedKey,
    })
  }

  return reKeyPayloads
}

/**
 * Handle a received encrypted sender key from a peer.
 * Decrypts it using the shared secret and installs it in both
 * the sender-keys store and the SFrame worker.
 */
export async function handleReceivedKey(
  peerId: string,
  epoch: number,
  encryptedKeyData: ArrayBuffer,
): Promise<void> {
  const peer = peers.get(peerId)
  if (!peer) throw new Error(`Unknown peer: ${peerId}`)

  const rawKey = await decryptSenderKey(peer.sharedSecret, encryptedKeyData)
  await installReceivedKey(peerId, epoch, rawKey)

  // Also install in worker for frame decryption
  const keyId = participantIdToKeyId(peerId)
  postWorkerCommand({
    type: 'install-key',
    keyId,
    rawKey,
    participantId: peerId,
  })
}

// ============================================================
// Worker Helpers
// ============================================================

function installKeyInWorker(participantId: string, senderKey: SenderKeyPair): void {
  if (!worker) return
  const cmd: SFrameInstallKeyCommand = {
    type: 'install-key',
    keyId: senderKey.keyId,
    rawKey: senderKey.rawKey,
    participantId,
  }
  worker.postMessage(cmd)
}

function setLocalKeyIdInWorker(keyId: number): void {
  if (!worker) return
  const cmd: SFrameSetLocalKeyIdCommand = {
    type: 'set-local-key-id',
    keyId,
  }
  worker.postMessage(cmd)
}

function postWorkerCommand(cmd: SFrameWorkerCommand): void {
  if (!worker) return
  worker.postMessage(cmd)
}

// ============================================================
// Teardown
// ============================================================

/**
 * Destroy the encryption session: terminate worker, clear all keys.
 */
export function teardownEncryption(): void {
  if (worker) {
    worker.terminate()
    worker = null
  }
  clearAllKeys()
  peers.clear()
  localIdentity = null
}
